import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fnfAPI, employeeAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
  Calculator, 
  Download, 
  CheckCircle2, 
  AlertCircle,
  Search,
  User,
  Calendar,
  IndianRupee,
  History,
  FileText
} from 'lucide-react';
import { format } from 'date-fns';

const FnFManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [employeeId, setEmployeeId] = useState(location.state?.employee_id || '');
  const [exitDate, setExitDate] = useState(location.state?.exit_date || '');
  const [remarks, setRemarks] = useState('');
  
  const [employee, setEmployee] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (location.state?.employee_id) {
      handleCalculate();
    }
  }, []);

  const handleFetchEmployee = async () => {
    if (!employeeId) return;
    try {
      const response = await employeeAPI.getEmployee(employeeId);
      setEmployee(response.data);
    } catch (error) {
      toast.error('Employee not found');
      setEmployee(null);
    }
  };

  const handleCalculate = async () => {
    if (!employeeId || !exitDate) {
      toast.error('Please enter Employee ID and Exit Date');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fnfAPI.calculate(employeeId, { exit_date: exitDate });
      setCalculation(response.data);
      if (!employee) handleFetchEmployee();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Calculation failed');
    } finally {
      setLoading(false);
    }
  };

    const handleFinalize = async () => {
    setProcessing(true);
    try {
      await fnfAPI.finalize(employeeId, { 
        ...calculation,
        reason: calculation.reason || "Resignation", // Fallback reason
        remarks: remarks 
      });
      toast.success('Full & Final Settlement finalized successfully');
      // Refresh to show status
      handleCalculate();
    } catch (error) {
      toast.error('Finalization failed');
    } finally {
      setProcessing(false);
    }
  };

  const downloadSettlement = async () => {
    try {
      const response = await fnfAPI.downloadPdf(employeeId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FnF_${employeeId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Full & Final Settlement</h1>
          <p className="text-muted-foreground">Calculate and process final dues for exiting employees.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Form */}
        <Card className="lg:col-span-1 shadow-sm border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Settlement Details</CardTitle>
            <CardDescription>Enter exit information to begin.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="employeeId">Employee ID</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input 
                  id="employeeId" 
                  placeholder="EMP001" 
                  className="pl-9"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  onBlur={handleFetchEmployee}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="exitDate">Exit Date / LWD</Label>
              <Input 
                id="exitDate" 
                type="date"
                value={exitDate}
                onChange={(e) => setExitDate(e.target.value)}
              />
            </div>

            <Button 
              className="w-full bg-primary hover:bg-slate-800" 
              onClick={handleCalculate}
              disabled={loading}
            >
              {loading ? 'Calculating...' : <><Calculator className="mr-2 h-4 w-4" /> Calculate Dues</>}
            </Button>

            {employee && (
              <div className="mt-6 pt-6 border-t border-slate-100 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="font-semibold text-sm">{employee.first_name} {employee.last_name}</p>
                  <p className="text-xs text-muted-foreground">{employee.designation} • {employee.department}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Calculation Result */}
        <Card className="lg:col-span-2 shadow-md border-slate-200 min-h-[400px]">
          {calculation ? (
            <>
              <CardHeader className="bg-slate-50/50 border-b border-slate-100">
                <div className="flex items-center justify-between w-full">
                  <div>
                    <CardTitle className="text-xl text-slate-900">Settlement Summary</CardTitle>
                    <CardDescription>Draft for {calculation.employee_name}</CardDescription>
                  </div>
                  {calculation.finalized && (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-none px-3 py-1">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Finalized
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="p-6 grid grid-cols-2 gap-8">
                  {/* Earnings */}
                  <div className="space-y-4">
                    <h3 className="font-bold text-sm uppercase tracking-wider text-green-700 flex items-center bg-green-50/50 p-2 rounded">
                      <IndianRupee className="h-4 w-4 mr-2" /> Earnings (A)
                    </h3>
                    <div className="space-y-2">
                       <div className="flex justify-between text-sm py-1 border-b border-slate-50 italic">
                        <span>Notice Pay / Unpaid Salary</span>
                        <span>₹{calculation.unpaid_salary.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm py-1 border-b border-slate-50">
                        <span>Leave Encashment</span>
                        <span>₹{calculation.leave_encashment.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm py-1 border-b border-slate-50">
                        <span>Gratuity</span>
                        <span>₹{calculation.gratuity.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm py-1 border-b border-slate-50">
                        <span>Bonus</span>
                        <span>₹{calculation.bonus.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-green-700 pt-2 text-lg">
                        <span>Total Earnings</span>
                        <span>₹{calculation.total_earnings.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Deductions */}
                  <div className="space-y-4">
                    <h3 className="font-bold text-sm uppercase tracking-wider text-red-700 flex items-center bg-red-50/50 p-2 rounded">
                      <AlertCircle className="h-4 w-4 mr-2" /> Recoveries (B)
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm py-1 border-b border-slate-50 italic">
                        <span>Salary Advance Recovery</span>
                        <span className="text-red-600">- ₹{calculation.advance_recovery.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm py-1 border-b border-slate-50">
                        <span>Notice Period Penalty</span>
                        <span className="text-red-600">- ₹0.00</span>
                      </div>
                      <div className="flex justify-between text-sm py-1 border-b border-slate-50">
                        <span>Other Deductions</span>
                        <span className="text-red-600">- ₹0.00</span>
                      </div>
                      <div className="flex justify-between font-bold text-red-700 pt-2 text-lg">
                        <span>Total Deductions</span>
                        <span>₹{calculation.advance_recovery.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mx-6 p-6 rounded-xl bg-slate-900 text-white flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm font-medium">Net Payable Amount (A - B)</p>
                    <p className="text-3xl font-bold tracking-tight">₹{calculation.net_payable.toFixed(2)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-400 text-sm">Exit Date</p>
                    <p className="font-semibold">{format(new Date(calculation.exit_date), 'dd MMMM, yyyy')}</p>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <div className="space-y-2">
                    <Label>Final Settlement Remarks</Label>
                    <Input 
                      placeholder="Enter remarks for the settlement..." 
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      disabled={calculation.finalized}
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4 border-t border-slate-100">
                    {!calculation.finalized ? (
                      <>
                        <Button 
                          variant="outline" 
                          onClick={() => setCalculation(null)} 
                          className="flex-1 border-slate-200"
                        >
                          Discard
                        </Button>
                        <Button 
                          className="flex-1 bg-green-600 hover:bg-green-700" 
                          onClick={handleFinalize}
                          disabled={processing}
                        >
                          {processing ? 'Finalizing...' : <><History className="mr-2 h-4 w-4" /> Finalize & Post to Payroll</>}
                        </Button>
                      </>
                    ) : (
                      <div className="w-full space-y-4">
                        <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg shadow-inner">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                              <CheckCircle2 className="h-6 w-6 text-green-600" />
                            </div>
                            <div>
                              <p className="font-bold text-green-800">Settlement Finalized</p>
                              <p className="text-xs text-green-600">The record has been saved and employee access disabled.</p>
                            </div>
                          </div>
                          <Button 
                            className="bg-primary hover:bg-slate-800 text-white shadow-md transition-all hover:scale-105"
                            onClick={downloadSettlement}
                          >
                            <Download className="mr-2 h-4 w-4" /> Download Statement
                          </Button>
                        </div>
                        <Button 
                          variant="ghost" 
                          className="w-full text-slate-500 hover:text-slate-700"
                          onClick={() => setCalculation(null)}
                        >
                          Process Another Settlement
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center p-10 space-y-4">
              <div className="h-16 w-16 rounded-full bg-slate-50 flex items-center justify-center">
                <FileText className="h-8 w-8 text-slate-300" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">No Calculation Found</h3>
                <p className="text-slate-500 max-w-[300px] mt-1">
                  Enter an employee ID and exit date on the left to start the Full & Final settlement process.
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default FnFManagement;
